/*
  <product-list-scroller> : rangée de cartes produits à défilement horizontal libre.

  - Le défilement lui-même est natif (doigt, pavé tactile, molette + Maj) : aucun calage.
  - Souris : on attrape la rangée et on la fait glisser, avec un élan qui ralentit progressivement.
  - Barre de progression : écrit --carousel-total / --xo-per-view / --xo-pagination-progress,
    lus par carousel-footer.css (largeur du curseur = part visible, position = progression).
  - Flèches : défilement doux d'environ une carte, estompées en bout de rangée.
*/
(function () {
  if (customElements.get('product-list-scroller')) return;

  class ProductListScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      if (!this.track) return;

      this.frame = null;
      this.update = this.update.bind(this);
      this.requestUpdate = this.requestUpdate.bind(this);

      this.track.addEventListener('scroll', this.requestUpdate, { passive: true });
      this.resizeObserver = new ResizeObserver(this.requestUpdate);
      this.resizeObserver.observe(this.track);

      if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
      if (this.next) this.next.addEventListener('click', () => this.step(1));

      this.bindDrag();
      this.update();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.momentum);
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = Math.max(scrollWidth - clientWidth, 0);
      const ratio = scrollWidth > 0 ? Math.min(clientWidth / scrollWidth, 1) : 1;
      const progress = max > 0 ? Math.min(Math.max(scrollLeft / max, 0), 1) : 0;

      this.style.setProperty('--carousel-total', '1');
      this.style.setProperty('--xo-per-view', ratio.toFixed(4));
      this.style.setProperty('--xo-pagination-progress', (progress * 100).toFixed(2) + '%');

      this.toggleAttribute('data-no-overflow', max < 2);
      if (this.prev) this.prev.disabled = scrollLeft <= 1;
      if (this.next) this.next.disabled = scrollLeft >= max - 1;
    }

    stepSize() {
      const item = this.track.firstElementChild;
      if (!item) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    step(direction) {
      cancelAnimationFrame(this.momentum);
      this.track.scrollBy({ left: direction * this.stepSize(), behavior: 'smooth' });
    }

    bindDrag() {
      const track = this.track;
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let lastX = 0;
      let lastTime = 0;
      let velocity = 0;
      let moved = false;

      track.addEventListener('dragstart', (event) => event.preventDefault());

      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        cancelAnimationFrame(this.momentum);
        pointerId = event.pointerId;
        startX = lastX = event.clientX;
        startScroll = track.scrollLeft;
        lastTime = performance.now();
        velocity = 0;
        moved = false;
      });

      track.addEventListener('pointermove', (event) => {
        if (event.pointerId !== pointerId) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          this.setAttribute('data-dragging', '');
          track.setPointerCapture(pointerId);
        }
        if (!moved) return;
        const now = performance.now();
        const dt = Math.max(now - lastTime, 1);
        velocity = (event.clientX - lastX) / dt; // px par ms
        lastX = event.clientX;
        lastTime = now;
        track.scrollLeft = startScroll - dx;
      });

      const end = (event) => {
        if (event.pointerId !== pointerId) return;
        pointerId = null;
        if (!moved) return;
        // Laisse passer ce relâchement sans déclencher de clic sur une carte
        const swallowClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
        };
        track.addEventListener('click', swallowClick, { capture: true, once: true });
        setTimeout(() => track.removeEventListener('click', swallowClick, { capture: true }), 0);
        this.removeAttribute('data-dragging');
        this.glide(velocity);
      };
      track.addEventListener('pointerup', end);
      track.addEventListener('pointercancel', end);
    }

    // Élan après un glisser à la souris : la vitesse décroît jusqu'à l'arrêt
    glide(velocity) {
      let v = -velocity * 16; // px par image
      const tick = () => {
        if (Math.abs(v) < 0.5) return;
        const before = this.track.scrollLeft;
        this.track.scrollLeft = before + v;
        if (this.track.scrollLeft === before) return; // bout de rangée
        v *= 0.94;
        this.momentum = requestAnimationFrame(tick);
      };
      this.momentum = requestAnimationFrame(tick);
    }
  }

  customElements.define('product-list-scroller', ProductListScroller);
})();
